/**
 * jingle-studio/index.js — the engine the page drives.
 *
 * The page owns the DOM; this owns the project, the caches, the voice lanes and
 * the render pipeline, and reports back through `on(event, fn)`.
 *
 * CACHING IS THE FEATURE, NOT AN OPTIMISATION. A studio is a thing you fiddle
 * with: nudge the bed level, try another character, re-seed the bed. A naive
 * implementation re-synthesises everything on every change, and at ~460 ms for
 * a bed plus ~200-400 ms for the voice chain that is a tool nobody will fiddle
 * with. So the pipeline is split at its natural seams and each stage is keyed
 * by exactly the inputs that change it:
 *
 *     text+voice+rate  → voiceRaw    (network)
 *     voiceRaw+character+pitch → voiceChained
 *     style+bpm+bars+seed+intensity → bed
 *     everything → mix
 *
 * Moving the voice fader re-runs only the last stage.
 */

import { projectForFormat, formatDef, FORMATS, fitCheck, voiceStartFor, LOUDNESS_TARGETS } from './structure.js';
import { renderJingle } from './render.js';
import { BED_STYLES, bedStyle, barSeconds, barsForSeconds } from './bed-synth.js';
import { SFX_TYPES, sfxDef, synthSfx } from './sfx-synth.js';
import { VOICE_CHARACTERS, ANNOUNCER_PRESETS, announcerPreset, resolveAnnouncerVoice } from './voice-fx.js';
import { encodeOutput, encodeStems, deliverOne, deliverMany, jingleFilename, OUTPUT_FORMATS } from './export.js';
import {
    fromAudioBuffer, toAudioBuffer, clipDuration, speechBounds, sliceClip, toChannels,
    makeClip, mixInto, timeStretch, rmsOf, applyGain,
} from './dsp.js';
import { encodeWav } from './wav.js';

const CFG = (typeof window !== 'undefined' && window.__JINGLE_CFG) || {};

/* ─────────────────────────────── audio context ───────────────────────── */

/**
 * Is Web Audio actually usable here?
 *
 * timbrica-core installs a stub constructor that THROWS on browsers without
 * Web Audio, so `typeof window.AudioContext === 'function'` is not an answer.
 * The studio itself needs no AudioContext to build or export a jingle — the
 * synthesis, the mix and the encoders are all plain JS — so a browser without
 * it should lose PLAYBACK and keep everything else, instead of looking broken.
 */
let _audioOk = null;
export function audioAvailable() {
    if (_audioOk !== null) return _audioOk;
    try {
        const AC = window.AudioContext || window.webkitAudioContext;
        if (typeof AC !== 'function') return (_audioOk = false);
        const probe = new AC();
        if (probe.close) { try { probe.close(); } catch (_) { /* ignore */ } }
        return (_audioOk = true);
    } catch (_) { return (_audioOk = false); }
}

let _ctx = null;
function ctx() {
    if (!_ctx) {
        const AC = window.AudioContext || window.webkitAudioContext;
        _ctx = new AC();
    }
    if (_ctx.state === 'suspended') { try { _ctx.resume(); } catch (_) { /* gesture will do it */ } }
    return _ctx;
}

async function decodeToClip(arrayBuffer) {
    const c = ctx();
    const ab = await new Promise((res, rej) => {
        const p = c.decodeAudioData(arrayBuffer, res, rej);
        if (p && p.then) p.then(res, rej);
    });
    return fromAudioBuffer(ab);
}

/* ──────────────────────────────── the engine ─────────────────────────── */

const listeners = {};
function emit(name, data) { (listeners[name] || []).forEach((f) => { try { f(data); } catch (e) { console.error(e); } }); }

const state = {
    project: projectForFormat('djdrop'),
    // 'none' until a take actually exists. Starting at 'tts' made the state
    // claim a voice the studio did not have, which is exactly the kind of
    // optimistic default a test then reports as success.
    voiceSource: 'none',             // none | tts | mic | upload
    voiceText: '',
    voiceId: CFG.defaultVoice || '',
    voiceLane: 'edge',               // edge | silero
    // Announcers beyond the lead, and how they are put together. Empty by
    // default: one voice stays the whole tool for anyone who wants one voice.
    extraVoiceIds: [],
    voiceBlend: 'together',          // together (все разом) | relay (по очереди)
    rate: 0,
    stockBed: null,                  // decoded clip when the user picks a stock track
    stockKey: null,
    result: null,
    rendering: false,
    dirty: true,
};

const cache = {
    // Дубль каждого диктора отдельно — чтобы переключение режима и добавление
    // голоса не переозвучивали уже приехавшее (и не тратили символы дважды).
    takes: {},
    voiceRawKey: null, voiceRaw: null,
    // Ключ сведения, собранного НЕПОЛНЫМ составом: такое не переиспользуется.
    voicePartialKey: null,
    voiceChainedKey: null, voiceChained: null,
    bedRawKey: null, bedRaw: null, bedStemsRaw: null,
};

function projectKey(p) {
    return JSON.stringify([p.format, p.seconds, p.bpm, p.bed, p.voice, p.fx, p.master]);
}

/* ─────────────────────────────── voice lanes ─────────────────────────── */

function csrf() {
    const m = document.querySelector('meta[name="csrf-token"]');
    return m ? m.content : '';
}

/**
 * Synthesize through whichever lane the chosen voice belongs to.
 *
 * Edge voice ids look like `ru-RU-DmitryNeural` and go through the tool's own
 * ajax route; our server voices (Silero, MeloTTS, Kokoro) go through
 * /api/tts/synthesize. Both are the same lanes /text-to-speech uses, so the
 * daily character pot meters this page too — which is deliberate: the only part
 * of a jingle that costs us anything is already counted, and nothing else here
 * needs a cap of its own.
 */
async function synthesizeVoice(text, voiceId, rate) {
    const isEdge = /^[a-z]{2,3}-[A-Z]{2,3}-\w+Neural$/.test(voiceId);
    if (isEdge) {
        const resp = await fetch(CFG.ttsAjaxUrl, {
            method: 'POST', credentials: 'same-origin',
            headers: {
                'Content-Type': 'application/json', 'X-CSRF-TOKEN': csrf(),
                'X-Requested-With': 'XMLHttpRequest', 'Accept': '*/*',
            },
            // `format` on this lane is an EDGE STREAM FORMAT NAME, not a file
            // extension: the handler validates it against a two-item list and
            // answers 422 to anything else. Sending 'mp3' looked reasonable and
            // was rejected on every call.
            body: JSON.stringify({ text, voice: voiceId, rate: rate | 0, pitch: 0, style: '', format: 'audio-24khz-96kbitrate-mono-mp3' }),
        });
        if (resp.redirected) throw voiceError('redirect');
        if (resp.status === 429) throw await rateOrQuota(resp);
        if (!resp.ok) throw voiceError('failed', resp.status);
        const ct = resp.headers.get('content-type') || '';
        if (ct.indexOf('json') >= 0) {
            // 202 = queued. The jingle lane is short text; queuing means the
            // service is hot, and telling the user that is better than spinning.
            throw voiceError('busy');
        }
        return await resp.arrayBuffer();
    }
    const resp = await fetch('/api/tts/synthesize', {
        method: 'POST', credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json, audio/wav', 'X-CSRF-TOKEN': csrf(), 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ text, language: (CFG.voiceLang && CFG.voiceLang[voiceId]) || 'ru', voice: voiceId, format: 'mp3', rate: rate | 0, pitch: 0 }),
    });
    if (resp.redirected) throw voiceError('redirect');
    if (resp.status === 429) throw await rateOrQuota(resp);
    if (resp.status === 404) throw voiceError('unavailable');
    if (!resp.ok) throw voiceError('failed', resp.status);
    return await resp.arrayBuffer();
}

/**
 * 429 — это ДВА разных отказа, и ответы на них противоположные.
 *
 * Суточный запас символов кончился — «на сегодня всё», ждать бессмысленно.
 * Частота запросов (30/мин на адрес) — «подождите минуту», и всё вернётся.
 * Тул до этого показывал на оба «на сегодня озвучка исчерпана»; с несколькими
 * дикторами вторая дверь стучится ВТРОЕ чаще (три запроса на одну озвучку), и
 * поймана она была именно так: на проде третий голос отвалился с 429, а тул
 * объяснил это исчерпанным днём.
 */
async function rateOrQuota(resp) {
    let daily = false;
    try {
        const ct = resp.headers.get('content-type') || '';
        if (ct.indexOf('json') >= 0) {
            const j = await resp.clone().json();
            daily = (j && (j.error === 'daily_quota' || j.gate === 'day')) || false;
        }
    } catch (_) { /* тело не разобралось — считаем частотой: она поправима */ }
    if (daily) return voiceError('quota', 429);
    const wait = parseInt(resp.headers.get('Retry-After') || '', 10);
    const e = voiceError('rate', 429);
    e.retryAfter = isFinite(wait) && wait > 0 ? Math.min(wait, 120) : 60;
    return e;
}

function voiceError(kind, status) {
    const e = new Error('voice_' + kind);
    e.kind = kind; e.status = status;
    return e;
}

/* ─────────────────────────────── render worker ───────────────────────── */

/**
 * The render runs off the main thread.
 *
 * Measured on a 4×-throttled phone: a bed-style change blocked the main thread
 * for 5 755 ms in ONE uninterrupted block — no scrolling, no taps, and no
 * spinner either, because the spinner is painted by the thread that is busy.
 * The DSP is plain Float32Array code, so moving it is a relocation.
 *
 * Everything degrades to a main-thread render: an engine without module
 * workers, a worker that fails to boot, a job that throws. The tool must work
 * slowly rather than not at all.
 */
let _worker = null, _workerDead = false, _jobId = 0;
const _jobs = new Map();

function workerUrl() { return CFG.workerUrl || ''; }

function workerDiag(issue, msg) {
    try { window.__aeDiagnostic && window.__aeDiagnostic({ issue, msg: String(msg || '').slice(0, 120) }); } catch (_) { /* telemetry never breaks the tool */ }
}

function getWorker() {
    if (_worker || _workerDead || !workerUrl()) return _worker;
    /* An engine without module workers (Firefox < 114, Safari < 15) would start the
     * render worker as a classic script that dies on its first import. The jingle
     * came out anyway on the main thread, but under the shared «something went
     * wrong» toast (measured on an emulated engine, 08.10.2026). Asked first, the
     * page goes straight to the main-thread render. */
    if (window.__moduleWorkerOk && !window.__moduleWorkerOk()) {
        _workerDead = true;
        workerDiag('jingle_worker_skipped', 'module_worker');
        emit('worker-fallback');
        return null;
    }
    try {
        _worker = new Worker(workerUrl(), { type: 'module' });
        _worker.onmessage = (ev) => {
            const m = ev.data || {};
            const job = _jobs.get(m.id);
            if (!job) return;
            _jobs.delete(m.id);
            if (m.type === 'error') job.reject(new Error(m.message));
            else job.resolve(m);
        };
        _worker.onerror = (e) => {
            // A worker that cannot boot must not take the tool with it: the render goes
            // to the main thread, so the failure is ours and is not repeated on the page
            // (uncancelled it would reach window.onerror — the shared toast, Sentry).
            if (e && e.preventDefault) e.preventDefault();
            workerDiag('jingle_worker_failed', e && e.message);
            _workerDead = true;
            for (const [, job] of _jobs) job.reject(new Error('worker_failed'));
            _jobs.clear();
            try { _worker.terminate(); } catch (_) { /* ignore */ }
            _worker = null;
            emit('worker-fallback');
        };
        post({ type: 'init', libs: CFG.workerLibs || [] }).catch(() => { _workerDead = true; });
    } catch (_) {
        _workerDead = true;
        _worker = null;
    }
    return _worker;
}

function post(msg, transfer) {
    const w = getWorker();
    if (!w) return Promise.reject(new Error('no_worker'));
    const id = ++_jobId;
    return new Promise((resolve, reject) => {
        _jobs.set(id, { resolve, reject });
        try { w.postMessage({ ...msg, id }, transfer || []); }
        catch (e) { _jobs.delete(id); reject(e); }
    });
}

/** Clips cross the boundary as plain channel arrays. */
function clipToMsg(clip) {
    return clip ? { sampleRate: clip.sampleRate, channels: clip.channels.map((c) => Float32Array.from(c)) } : null;
}
function msgToClip(m) {
    return m ? { sampleRate: m.sampleRate, channels: m.channels.map((c) => (c instanceof Float32Array ? c : new Float32Array(c))) } : null;
}

/* ─────────────────────────────── render loop ─────────────────────────── */

let renderToken = 0;
let renderTimer = null;

/**
 * Schedule a render. Coalesces bursts (a slider drag fires dozens of input
 * events) and abandons stale runs by token — the alternative is a queue of
 * renders whose last-finished wins, which is how a preview ends up showing a
 * setting the user has already changed.
 */
let settleTimer = null;

// An export renders every variant through the same function. While it runs, a
// render is not a preview and must not touch what the transport is playing.
let exporting = false;

/**
 * Should this render carry the monitor buses?
 *
 * For anything short enough to hold, always — so the FIRST press of «only the
 * voice» is instant instead of costing a render. They are nearly free to
 * produce (the buses are sums of buffers the render already has; no synthesis
 * repeats) and the only real cost is holding them: 45 s is 32 MB for the pair,
 * which covers every jingle format the tool ships. Past that — a two-minute ad
 * spot would add 84 MB to a mix a phone is already carrying — they are built
 * only while a solo is actually engaged.
 */
function wantBuses() {
    return !!monitor || state.project.seconds <= 45;
}

function scheduleRender(delay = 130) {
    clearTimeout(renderTimer);
    clearTimeout(settleTimer);
    renderTimer = setTimeout(() => { render({ quick: true, buses: wantBuses() }); }, delay);
    // …then settle. The quick render is for the ear while a control is moving;
    // the settle render is the one whose loudness number is final. Without the
    // second pass the readout would sit ~1 dB off target forever, and a user
    // comparing it against their own meter would be right to distrust us.
    settleTimer = setTimeout(() => { render({ quick: false, buses: wantBuses() }); }, delay + 900);
}

async function render(opts = {}) {
    const token = ++renderToken;
    state.rendering = true;
    emit('rendering', true);
    try {
        const p = state.project;

        if (getWorker()) {
            try {
                const m = await post({ type: 'render', project: JSON.parse(JSON.stringify(p)), opts: { stems: !!opts.stems, buses: !!opts.buses, quick: !!opts.quick }, libs: CFG.workerLibs || [] });
                if (token !== renderToken) return null;
                const res = {
                    clip: msgToClip(m.clip), duration: m.duration, voiceStart: m.voiceStart,
                    voiceSec: m.voiceSec, lufs: m.lufs, truePeak: m.truePeak,
                    loudnessApplied: m.loudnessApplied, voiceTruncated: m.voiceTruncated,
                    masterGain: m.masterGain,
                    stems: null, buses: null,
                };
                if (m.stems) {
                    res.stems = {};
                    for (const k of Object.keys(m.stems)) res.stems[k] = msgToClip(m.stems[k]);
                }
                if (m.buses) {
                    res.buses = {};
                    for (const k of Object.keys(m.buses)) res.buses[k] = msgToClip(m.buses[k]);
                }
                state.result = res;
                state.dirty = false;
                emit('rendered', res);
                emit('preview-quality', opts.quick ? 'quick' : 'final');
                if (playSource && !opts.quick && !exporting) swapPlayback();
                return res;
            } catch (e) {
                // Fall through to the main thread — measurably worse, still working.
                _workerDead = true;
                emit('worker-fallback');
            }
        }
        const sources = {};
        if (cache.voiceRaw && state.voiceSource !== 'none') sources.voice = cache.voiceRaw;
        if (p.bed.source === 'stock' && state.stockBed) sources.stockBed = state.stockBed;

        // Reuse the two expensive stages when the settings that produce them
        // have not moved. Both keys name every input of their stage and nothing
        // else — a key that is too coarse silently serves a stale bed, and one
        // that is too fine throws the cache away on every fader move.
        const vKey = cache.voiceRawKey + '|' + p.voice.character + '|' + p.voice.pitchExtra;
        if (cache.voiceChainedKey === vKey && cache.voiceChained) sources.voiceChained = cache.voiceChained;
        const bKey = [p.bed.source, p.bed.style, p.bpm, p.bed.seed, p.bed.intensity, state.stockKey,
            Math.round(p.seconds * 20), p.output.stems ? 1 : 0].join('|');
        if (cache.bedRawKey === bKey && cache.bedRaw) {
            sources.bedRaw = cache.bedRaw;
            sources.bedStemsRaw = cache.bedStemsRaw;
        }

        const res = await renderJingle(p, sources, {
            sampleRate: 44100,
            stems: !!opts.stems,
            buses: !!opts.buses,
            quick: !!opts.quick,
            pitchFn: pitchViaEngine,
        });
        if (token !== renderToken) return null;      // superseded
        if (res.prepared) {
            if (res.prepared.voiceChained) { cache.voiceChained = res.prepared.voiceChained; cache.voiceChainedKey = vKey; }
            if (res.prepared.bedRaw) { cache.bedRaw = res.prepared.bedRaw; cache.bedStemsRaw = res.prepared.bedStemsRaw; cache.bedRawKey = bKey; }
        }
        state.result = res;
        state.dirty = false;
        emit('rendered', res);
        if (opts.quick) emit('preview-quality', 'quick'); else emit('preview-quality', 'final');
        if (playSource && !opts.quick && !exporting) swapPlayback();
        return res;
    } catch (err) {
        if (token === renderToken) emit('error', err);
        return null;
    } finally {
        if (token === renderToken) { state.rendering = false; emit('rendering', false); }
    }
}

/**
 * Use the site's Signalsmith pitch engine when the page has loaded it — it is
 * better than the built-in SOLA on large shifts. Falling back is silent and
 * safe; falling back is also what happens in the node tests.
 */
let _peLoading = null;
function loadPitchEngine() {
    if (window.PitchEngine) return Promise.resolve(true);
    if (_peLoading) return _peLoading;
    if (!CFG.pitchEngineUrl) return Promise.resolve(false);
    // Loaded on demand, not on every page view: only two of the nine voice
    // characters shift pitch, and its own vendor file is another 224 KB. The
    // URL is same-origin (the vendored Signalsmith build) — nothing here ever
    // reaches an external CDN, which is what keeps the tool working when
    // outbound CDNs are unreachable.
    _peLoading = new Promise((resolve) => {
        const el = document.createElement('script');
        el.src = CFG.pitchEngineUrl;
        el.onload = () => resolve(!!window.PitchEngine);
        el.onerror = () => resolve(false);
        document.head.appendChild(el);
    });
    return _peLoading;
}

/* ───────────────────────────── несколько дикторов ────────────────────── */

/**
 * Сколько голосов пускаем в один джингл.
 *
 * Три — не круглое число, а край измеренного: на восьми дублях Silero,
 * прогнанных через тот же GigaAM, что стоит у нас на распознавании,
 * выровненный унисон стоил +0.03 CER на двух и трёх голосах и +0.05 на
 * четырёх, а разборчивость ведущего (ESTOI) падала 0.65 → 0.52 → 0.39.
 * Четвёртый голос перестаёт добавлять «толпу» и начинает съедать слова.
 */
export const MAX_EXTRA_VOICES = 2;

/**
 * Годится ли этот набор для унисона.
 *
 * Мужской с женским В УНИСОН — единственная связка, которая разошлась с
 * остальными на ОБЕИХ осях: ΔCER +0.08…+0.15 против +0.01…+0.04 у
 * однополых, и в расшифровках слышно, чем платим («Слушай, качай» →
 * «Слушай, кончай»). Тембры слишком далеко, чтобы слиться в один хор.
 * По очереди и «подпевкой» смешанная пара измерилась чисто (ΔCER 0.000),
 * поэтому запрета нет — есть подсказка и другой режим.
 */
export function unisonGenderMix(ids, genderOf) {
    const g = ids.map((id) => (genderOf ? genderOf(id) : '')).filter(Boolean);
    return g.indexOf('M') >= 0 && g.indexOf('F') >= 0;
}

/**
 * На какие куски рвётся текст в режиме «по очереди».
 *
 * Строки важнее запятых: перенос строки — единственный способ, которым
 * пользователь может РАСПОРЯДИТЬСЯ раскладкой, и если он его поставил,
 * значит имел в виду именно это. Дальше — предложения и запятые, и только
 * если делить нечего — по словам.
 *
 * Кусков может быть БОЛЬШЕ, чем дикторов: перекличка — это чередование
 * («ты — я — ты»), а не «каждому по трети». Раздача голосов — в voicePlan.
 */
export function splitScript(text, maxParts = 6) {
    const t = String(text || '').trim();
    if (!t) return [];

    const byLine = t.split(/\r?\n/).map((s) => s.trim()).filter(Boolean);
    if (byLine.length >= 2) return spread(byLine, maxParts);

    // Режем ПОСЛЕ знака препинания, оставляя его в куске. Без lookbehind: он
    // не работает в Safari до 16.4, а падает не разбор — падает вся страница.
    // Поэтому ставим сентинел за знаком и режем по нему.
    const SEP = String.fromCharCode(1);
    const byPunct = t.replace(/([.!?…,;:—–])\s+/g, '$1' + SEP)
        .split(SEP).map((s) => s.trim()).filter(Boolean);
    if (byPunct.length >= 2) return spread(byPunct, maxParts);

    const words = t.split(/\s+/).filter(Boolean);
    if (words.length >= 2) return spread(words, Math.min(maxParts, words.length));

    // Делить нечего — одно слово читает ведущий, режим сам сжимается.
    return [t];
}

/** Сливает куски до n штук, сохраняя порядок и не оставляя пустых. */
function spread(parts, n) {
    const groups = Math.max(1, Math.min(n, parts.length));
    const out = [];
    for (let i = 0; i < groups; i++) out.push([]);
    parts.forEach((p, i) => out[Math.floor((i * groups) / parts.length)].push(p));
    return out.map((g) => g.join(' ')).filter(Boolean);
}

/**
 * Складывает дубли в ОДИН голосовой клип.
 *
 * Всё, что ниже по конвейеру — характер голоса, приглушение музыки, сведение,
 * стемы, экспорт — продолжает видеть один голос, ровно как до этой правки.
 * Это не экономия, а способ не тащить многоголосье через шесть чужих файлов.
 *
 * ⚠ Длины выравниваются ОБЯЗАТЕЛЬНО. Один и тот же текст разные дикторы
 * читают с разбросом до 44 % (2.36 с против 3.40 с на «Радио Волна…»), и
 * наложение «как приехало» даёт эхо на хвосте: измерено +0.18 CER против
 * +0.03 у выровненного, в расшифровке — «повторяй.е повторяй».
 */
export function blendTakes(takes, mode) {
    const raw = takes.filter((t) => t && t.channels && t.channels[0].length);
    if (!raw.length) return null;
    if (raw.length === 1) return raw[0];
    const sr = raw[0].sampleRate;

    // ⚠ Сначала СРЕЗАЕМ тишину, потом выравниваем длину. Синтез добавляет
    // паузу в начале, и у каждого голоса она своя: подгонка длины такой дубль
    // не спасёт — она растянет и паузу, начала фраз всё равно разъедутся, и
    // получится ровно то наложение, которое в замере стоило +0.18 CER.
    // Дальше по конвейеру голос всё равно обрезается (render.js), но там уже
    // ОДИН склеенный дубль — поздно.
    const live = raw.map((c) => {
        const b = speechBounds(c);
        return b.speech ? sliceClip(c, b.start, b.end) : c;
    });

    if (mode === 'relay') {
        // Стык фраз — не встык: 90 мс дают услышать смену диктора как приём,
        // а не как склейку.
        const gap = 0.09;
        const total = live.reduce((s, c) => s + clipDuration(c), 0) + gap * (live.length - 1);
        const out = makeClip(total, sr, 2);
        let at = 0;
        for (const c of live) { mixInto(out, c, at, 1, 0); at += clipDuration(c) + gap; }
        return out;
    }

    // together: ведущий задаёт длину, остальные подгоняются под него.
    const lead = live[0];
    const leadLen = lead.channels[0].length;
    const out = makeClip(leadLen / sr, sr, 2);
    const rms0 = rmsOf(lead) || 1;
    live.forEach((c, i) => {
        const fitted = i === 0 ? c : timeStretch(c, leadLen);
        // Ведущий по центру и полной громкостью, остальные по бокам и тише —
        // так у джингла остаётся тот, кого слышно, а не каша из равных.
        const gain = (i === 0 ? 1 : 0.85) / Math.sqrt(live.length);
        const pan = i === 0 ? 0 : (i % 2 ? -0.5 : 0.5);
        mixInto(out, fitted, 0, gain, pan);
    });
    // Возвращаем сумме уровень ОДНОГО дубля. Не ради громкости — её дальше
    // всё равно задаёт пик-нормировка голосового тракта (applyVoiceChain), —
    // а ради ОБРЕЗКИ: границы речи ищутся по АБСОЛЮТНОМУ порогу −42 dBFS, и
    // микс, приехавший тише дубля, обрезался бы по краям иначе, чем один
    // голос, а за обрезкой едут и длина, и место голоса в такте.
    const rms1 = rmsOf(toChannels(out, 1));
    if (rms1 > 1e-6) applyGain(out, (rmsOf(toChannels(lead, 1)) || rms0) / rms1);
    return out;
}

function pushVoiceToWorker(clip, key) {
    if (!getWorker()) return;
    post({ type: 'voice', clip: clipToMsg(clip), key: key || '' }).catch(() => { /* falls back on next render */ });
}

function pushStockToWorker(clip) {
    if (!getWorker()) return;
    post({ type: 'stock', clip: clipToMsg(clip) }).catch(() => { /* falls back on next render */ });
}

async function pitchViaEngine(clip, semitones) {
    if (!window.PitchEngine) { try { await loadPitchEngine(); } catch (_) { /* SOLA */ } }
    const PE = window.PitchEngine;
    if (!PE || !PE.shift) return null;              // render.js falls back to SOLA
    try {
        const ab = toAudioBuffer(clip, ctx());
        const out = await PE.shift(ab, { semitones, preserveFormants: semitones < 0 });
        return fromAudioBuffer(out);
    } catch (_) { return null; }
}

/* ───────────────────────────────── playback ──────────────────────────── */

let playSource = null, playGain = null, playStart = 0, playOffset = 0, playDur = 0, rafId = 0;
let pausedAt = 0;
let looping = false;
let monitor = null;                     // null | 'voice' | 'music'

/**
 * Which audio the transport is playing right now.
 *
 * With a solo engaged this is one of the two monitor buses, lifted by the gain
 * the master applied to the mix — so a soloed side is heard at the level it has
 * IN the mix. It is deliberately not the mastered mix with one fader at zero:
 * that path re-normalises the surviving side back up to the loudness target and
 * hands back a balance nobody will ever ship.
 */
function monitorClip() {
    const r = state.result;
    if (!r) return null;
    if (!monitor || !r.buses || !r.buses[monitor]) return r.clip;
    return r.buses[monitor];
}
function monitorGain() {
    const r = state.result;
    if (!monitor || !r || !r.buses || !r.buses[monitor]) return 1;
    return (r.masterGain && isFinite(r.masterGain)) ? r.masterGain : 1;
}

/** Where the playhead is, whether or not the source is running. */
function positionNow() {
    if (!playSource) return pausedAt;
    const t = playOffset + (ctx().currentTime - playStart);
    if (looping && playDur > 0.05) return t % playDur;
    return Math.min(t, playDur);
}

/**
 * Stop the source. This is PAUSE: the position is kept, so the button that
 * reads «❚❚» resumes rather than starting the jingle over.
 *
 * `silent` exists because a restart goes through here too, and an internal
 * teardown must not announce itself as "playback stopped". Announcing it is
 * what made the loop button unusable: the page restarted playback from its own
 * 'playing:false', so every restart scheduled the next one — 21 restarts in
 * 1.5 s, measured — and the pause button could no longer get a word in.
 */
function stop(silent = false) {
    if (playSource) {
        pausedAt = positionNow();
        try { playSource.onended = null; playSource.stop(); } catch (_) { /* already ended */ }
        playSource = null;
    }
    cancelAnimationFrame(rafId);
    if (!silent) emit('playing', false);
}

function startSource(fromSec, fadeInSec) {
    const c = ctx();
    const clip = monitorClip();
    const src = c.createBufferSource();
    src.buffer = toAudioBuffer(clip, c);
    playDur = src.buffer.duration;
    if (fromSec >= playDur - 0.02) fromSec = 0;
    // Native looping, not a timer that restarts on 'ended': the seam is sample
    // accurate, a fader move during a loop is heard on the next pass, and
    // toggling it needs no restart at all.
    src.loop = looping;
    src.loopStart = 0;
    src.loopEnd = playDur;
    const g = c.createGain();
    g.gain.value = monitorGain();
    if (fadeInSec > 0) {
        g.gain.setValueAtTime(0, c.currentTime);
        g.gain.linearRampToValueAtTime(monitorGain(), c.currentTime + fadeInSec);
    }
    src.connect(g).connect(c.destination);
    src.onended = () => {
        if (playSource !== src) return;
        playSource = null; playGain = null;
        pausedAt = 0;
        cancelAnimationFrame(rafId);
        emit('playing', false);
        emit('playhead', 0);
    };
    playOffset = fromSec;
    playStart = c.currentTime;
    src.start(0, fromSec);
    playSource = src; playGain = g;
    return fromSec;
}

/**
 * `play()` resumes from where it was paused; `play(n)` starts at n seconds.
 * The waveform click and the keyboard shortcut pass a number; the transport
 * button does not.
 */
function play(fromSec) {
    if (!state.result) return;
    if (!audioAvailable()) { emit('no-audio'); return; }
    const from = typeof fromSec === 'number' && isFinite(fromSec) ? Math.max(0, fromSec) : pausedAt;
    stop(true);
    startSource(from, 0);
    emit('playing', true);
    const c = ctx();
    const tick = () => {
        if (!playSource) return;
        emit('playhead', positionNow());
        rafId = requestAnimationFrame(tick);
    };
    tick();
}

/**
 * Put a different buffer under a running transport at the same position.
 *
 * Used when a solo is engaged or dropped mid-playback — the complaint that
 * started this was «I pressed voice-only and it still plays everything», which
 * was true: the old code changed the project and left the already-scheduled
 * buffer running, so the solo arrived 2.2 s later and only on the next press of
 * play. Crossfaded over 25 ms because a hard cut between two different mixes at
 * an arbitrary sample is an audible click.
 */
function swapPlayback() {
    if (!playSource || !playGain) return;
    const at = positionNow();
    const c = ctx();
    const old = playSource, oldGain = playGain;
    const XF = 0.025;
    try {
        oldGain.gain.cancelScheduledValues(c.currentTime);
        oldGain.gain.setValueAtTime(oldGain.gain.value, c.currentTime);
        oldGain.gain.linearRampToValueAtTime(0, c.currentTime + XF);
        old.onended = null;
        old.stop(c.currentTime + XF + 0.01);
    } catch (_) { try { old.stop(); } catch (__) { /* gone */ } }
    playSource = null;
    startSource(at, XF);
}

/* ──────────────────────────────── public API ─────────────────────────── */

const api = {
    FORMATS, BED_STYLES, SFX_TYPES, VOICE_CHARACTERS, OUTPUT_FORMATS, LOUDNESS_TARGETS,
    ANNOUNCER_PRESETS, announcerPreset, resolveAnnouncerVoice,
    MAX_EXTRA_VOICES, splitScript, unisonGenderMix,

    on(name, fn) { (listeners[name] = listeners[name] || []).push(fn); return api; },
    state,
    get project() { return state.project; },

    /** Switch format: a whole new project, but the script and voice survive. */
    setFormat(key) {
        const voiceSec = cache.voiceRaw ? clipDuration(cache.voiceRaw) : 0;
        state.project = projectForFormat(key, { voiceSec });
        emit('project', state.project);
        scheduleRender(0);
    },

    patch(path, value) {
        const parts = path.split('.');
        let o = state.project;
        for (let i = 0; i < parts.length - 1; i++) o = o[parts[i]];
        o[parts[parts.length - 1]] = value;
        emit('project', state.project);
        scheduleRender();
    },

    /** Length in seconds; keeps bars consistent so the bed stays bar-exact. */
    setSeconds(sec) {
        state.project.seconds = Math.max(0.8, Math.min(120, sec));
        state.project.bars = barsForSeconds(state.project.seconds, state.project.bpm, 1);
        emit('project', state.project);
        scheduleRender();
    },

    setBars(bars) {
        state.project.bars = Math.max(1, Math.min(64, bars));
        state.project.seconds = barSeconds(state.project.bpm) * state.project.bars;
        emit('project', state.project);
        scheduleRender();
    },

    setBpm(bpm) {
        state.project.bpm = Math.max(60, Math.min(200, bpm));
        if (state.project.lengthMode === 'bars') state.project.seconds = barSeconds(state.project.bpm) * state.project.bars;
        else state.project.bars = barsForSeconds(state.project.seconds, state.project.bpm, 1);
        emit('project', state.project);
        scheduleRender();
    },

    reseedBed() {
        state.project.bed.seed = 1 + Math.floor(Math.random() * 99999);
        emit('project', state.project);
        scheduleRender(0);
    },

    addFx(type) {
        const def = sfxDef(type);
        const total = state.project.seconds;
        state.project.fx.push({
            id: 'fx' + Date.now().toString(36),
            type, seconds: Math.min(def.defSec, total * 0.6),
            at: Math.max(0, Math.min(total - 0.2, state.result ? state.result.voiceStart * 0.5 : 0)),
            gain: 0.85, intensity: 0.7, seed: 1 + Math.floor(Math.random() * 9999),
        });
        emit('project', state.project);
        scheduleRender(0);
    },

    removeFx(id) {
        state.project.fx = state.project.fx.filter((f) => f.id !== id);
        emit('project', state.project);
        scheduleRender(0);
    },

    patchFx(id, key, value) {
        const f = state.project.fx.find((x) => x.id === id);
        if (!f) return;
        if (key === 'atEnd') delete f.at; else if (key === 'at') delete f.atEnd;
        f[key] = value;
        emit('project', state.project);
        scheduleRender();
    },

    /** Preview one effect on its own — the palette should be auditionable. */
    async auditionSfx(type) {
        const clip = synthSfx({ type, seed: 1 + Math.floor(Math.random() * 9999), sampleRate: 44100, freq: 220 });
        const c = ctx();
        const src = c.createBufferSource();
        src.buffer = toAudioBuffer(clip, c);
        src.connect(c.destination);
        src.start();
    },

    /* ---- voice ---- */

    setVoiceText(text) { state.voiceText = text; emit('voice-stale', api.voiceStale()); },
    setVoiceId(id) { state.voiceId = id; emit('voice-stale', api.voiceStale()); },
    setRate(r) { state.rate = r; emit('voice-stale', api.voiceStale()); },

    /** Дикторы этого джингла: ведущий первым, дальше добавленные. */
    voiceIds() {
        const extra = (state.extraVoiceIds || []).filter((id) => id && id !== state.voiceId);
        return [state.voiceId].concat(extra.slice(0, MAX_EXTRA_VOICES));
    },

    setExtraVoiceIds(ids) {
        const seen = {};
        state.extraVoiceIds = (ids || []).filter((id) => {
            if (!id || id === state.voiceId || seen[id]) return false;
            seen[id] = 1;
            return true;
        }).slice(0, MAX_EXTRA_VOICES);
        emit('voices', { ids: api.voiceIds(), blend: state.voiceBlend });
        emit('voice-stale', api.voiceStale());
    },

    blend() { return state.voiceBlend; },

    setVoiceBlend(mode) {
        state.voiceBlend = mode === 'relay' ? 'relay' : 'together';
        emit('voices', { ids: api.voiceIds(), blend: state.voiceBlend });
        emit('voice-stale', api.voiceStale());
    },

    /** Кто что читает при текущих настройках — то же, что покажет страница. */
    voicePlan(text) {
        const t = String(text === undefined ? state.voiceText : text || '').trim();
        const ids = api.voiceIds();
        if (ids.length < 2 || !t) return [{ voiceId: ids[0], text: t }];
        if (state.voiceBlend === 'relay') {
            // Голоса идут по кругу: «ты — я — ты». Кусков может быть больше,
            // чем дикторов, и это норма переклички, а не ошибка раскладки.
            const parts = splitScript(t);
            return parts.map((part, i) => ({ voiceId: ids[i % ids.length], text: part }));
        }
        return ids.map((id) => ({ voiceId: id, text: t }));
    },

    /**
     * Does the mix contain the words that are on screen?
     *
     * Two ways it can silently not, both measured on the live page:
     *   'unvoiced' — text typed, «Озвучить» never pressed. The export came out
     *                with NO voice at all, while the filename carried the text
     *                and the fit hint read «Голос — 2.3 с из 7.5 с».
     *   'stale'    — text edited AFTER voicing. The export shipped the previous
     *                take, so the words in the file were not the words on the
     *                screen, with nothing on the page saying so.
     * A take that came from a file or the microphone is never stale: it is not
     * derived from the text box, so editing the text says nothing about it.
     */
    voiceStale() {
        const text = (state.voiceText || '').trim();
        if (state.voiceSource === 'upload' || state.voiceSource === 'mic') return null;
        if (!text) return null;
        if (!cache.voiceRaw) return 'unvoiced';
        return cache.voiceRawKey === api._voiceKey(text) ? null : 'stale';
    },

    /** Всё, что меняет ЗВУЧАНИЕ дубля: текст, набор дикторов, режим, темп. */
    _voiceKey(text) {
        const t = String(text === undefined ? state.voiceText : text || '').trim();
        return [t, state.voiceBlend, api.voiceIds().join('+'), state.rate].join('|');
    },

    /**
     * Fetch (or reuse) the raw take, then re-render.
     *
     * С несколькими дикторами это N запросов, а не один, поэтому каждый дубль
     * кэшируется ОТДЕЛЬНО: смена режима «вместе ↔ по очереди» и добавление
     * третьего голоса не переозвучивают то, что уже приехало, — а значит не
     * тратят второй раз суточный запас символов, которым лейн озвучки
     * меряется на всём сайте.
     */
    async speak() {
        const text = (state.voiceText || '').trim();
        if (!text) { api.clearVoice(); return null; }
        const key = api._voiceKey(text);
        // ⚠ Кэш отдаёт готовое сведение — но НЕ тогда, когда прошлый заход
        // потерял диктора. Иначе повторное нажатие не делает НИЧЕГО, а тул в
        // этот момент как раз обещает: «нажмите ещё раз, он добавится».
        // Поймано сторожем: повтор стоил ноль запросов и возвращал тот же
        // неполный микс. Пересборка дешёвая — готовые дубли лежат в cache.takes.
        if (cache.voiceRawKey === key && cache.voiceRaw && cache.voicePartialKey !== key) {
            scheduleRender(0);
            return cache.voiceRaw;
        }

        const plan = api.voicePlan(text);
        const relay = plan.length > 1 && state.voiceBlend === 'relay';
        emit('voice-loading', true);
        try {
            const clips = [];
            const dropped = [];
            let dropReason = null, dropWait = 0;
            for (const part of plan) {
                let clip = await api._takeFor(part.text, part.voiceId);
                if (!clip && relay) {
                    // В перекличке у каждого своя ФРАЗА: молча выбросить дубль
                    // значит выдать джингл без куска текста. Пробуем прочесть
                    // этот кусок ведущим — слова важнее раскладки по голосам.
                    clip = await api._takeFor(part.text, state.voiceId);
                    if (clip) dropped.push(part.voiceId);
                }
                if (clip) { clips.push(clip); continue; }
                // Ведущий не приехал (или перекличка потеряла фразу целиком) —
                // это отказ, а не полработы.
                if (!clips.length || relay) throw api._lastVoiceError || new Error('tts');
                dropped.push(part.voiceId);
                // ⚠ Причину надо ДОНЕСТИ. Замер на проде: бесплатному даётся
                // ПЯТЬ озвучек в минуту (rate_per_minute_free), а состав тратит
                // по запросу на голос — то есть трио упирается в лимит на
                // втором нажатии в ту же минуту, и теряется именно партнёр.
                // «Не отозвался» тут неправда: он отзовётся через :sec секунд,
                // и повтор будет стоить ОДИН запрос — остальные дубли в кэше.
                if (api._lastVoiceError && api._lastVoiceError.kind === 'rate') {
                    dropReason = 'rate';
                    dropWait = api._lastVoiceError.retryAfter || 60;
                }
            }

            const blended = blendTakes(clips, state.voiceBlend);
            if (!blended) throw api._lastVoiceError || new Error('tts');
            cache.voiceRaw = blended;
            cache.voiceRawKey = key;
            cache.voicePartialKey = dropped.length ? key : null;
            cache.voiceChained = null; cache.voiceChainedKey = null;
            pushVoiceToWorker(blended, key);
            state.voiceSource = 'tts';
            api._afterVoiceLoaded();
            if (dropped.length) emit('voice-partial', {
                dropped: dropped, used: clips.length, relay: relay,
                reason: dropReason, retryAfter: dropWait,
            });
            return blended;
        } catch (err) {
            if (typeof window !== 'undefined' && window.__ae) {
                window.__ae('tool_error', {
                    slug: 'jingle-maker', stage: 'tts', kind: err.kind || 'unknown',
                    status: err.status || 0, voices: plan.length, blend: state.voiceBlend,
                });
            }
            emit('voice-error', err);
            return null;
        } finally {
            emit('voice-loading', false);
        }
    },

    /** Один дубль: из кэша или из сети. null = не приехал (ошибка в _lastVoiceError). */
    async _takeFor(text, voiceId) {
        const t = String(text || '').trim();
        if (!t || !voiceId) return null;
        const pk = t + '|' + voiceId + '|' + state.rate;
        if (cache.takes[pk]) return cache.takes[pk];
        try {
            const buf = await synthesizeVoice(t, voiceId, state.rate);
            const clip = await decodeToClip(buf);
            const keys = Object.keys(cache.takes);
            if (keys.length > 12) delete cache.takes[keys[0]];
            cache.takes[pk] = clip;
            return clip;
        } catch (err) {
            api._lastVoiceError = err;
            return null;
        }
    },

    async useAudioFile(fileOrBuffer, source = 'upload') {
        const ab = fileOrBuffer instanceof ArrayBuffer ? fileOrBuffer : await fileOrBuffer.arrayBuffer();
        const clip = await decodeToClip(ab);
        cache.voiceRaw = clip;
        cache.voiceRawKey = 'file:' + Date.now();
        cache.voiceChained = null; cache.voiceChainedKey = null;
        pushVoiceToWorker(clip, cache.voiceRawKey);
        state.voiceSource = source;
        api._afterVoiceLoaded();
        return clip;
    },

    _afterVoiceLoaded() {
        const b = speechBounds(cache.voiceRaw);
        const sec = b.speech ? (b.end - b.start) : clipDuration(cache.voiceRaw);
        if (state.project.lengthMode === 'voice') {
            state.project.seconds = sec + 0.95;
        }
        emit('voice-ready', { seconds: sec, fit: fitCheck(state.project, sec) });
        emit('voice-stale', api.voiceStale());
        emit('project', state.project);
        scheduleRender(0);
    },

    clearVoice() {
        cache.voiceRaw = null; cache.voiceRawKey = null;
        cache.voiceChained = null; cache.voiceChainedKey = null;
        pushVoiceToWorker(null, '');
        state.voiceSource = 'none';
        emit('voice-ready', null);
        emit('voice-stale', api.voiceStale());
        scheduleRender(0);
    },

    get voiceSeconds() {
        if (!cache.voiceRaw) return 0;
        const b = speechBounds(cache.voiceRaw);
        return b.speech ? b.end - b.start : clipDuration(cache.voiceRaw);
    },

    fit() { return fitCheck(state.project, api.voiceSeconds); },

    /* ---- stock bed ---- */

    async useStockBed(key, url) {
        emit('bed-loading', true);
        try {
            const r = await fetch(url, { credentials: 'same-origin' });
            if (!r.ok) throw new Error('bed ' + r.status);
            state.stockBed = await decodeToClip(await r.arrayBuffer());
            state.stockKey = key;
            pushStockToWorker(state.stockBed);
            state.project.bed.source = 'stock';
            state.project.bed.stockKey = key;
            emit('project', state.project);
            scheduleRender(0);
        } catch (e) {
            emit('error', e);
        } finally { emit('bed-loading', false); }
    },

    async useBedFile(file) {
        state.stockBed = await decodeToClip(await file.arrayBuffer());
        state.stockKey = 'upload';
        pushStockToWorker(state.stockBed);
        state.project.bed.source = 'stock';
        state.project.bed.stockKey = 'upload';
        // A user's own bed usually has its own tempo; picking it up means a DJ
        // drop cut from it still lands on the beat.
        if (window.__detectBPM) {
            try {
                const bpm = await window.__detectBPM(toAudioBuffer(state.stockBed, ctx()));
                if (bpm && bpm.bpm) { api.setBpm(Math.round(bpm.bpm)); emit('bpm-detected', bpm.bpm); }
            } catch (_) { /* tempo is a nicety, not a requirement */ }
        }
        emit('project', state.project);
        scheduleRender(0);
    },

    /* ---- transport ---- */

    play, stop, audioAvailable,
    get isPlaying() { return !!playSource; },
    get isLooping() { return looping; },
    get monitor() { return monitor; },
    /** The audio the transport is on right now — the mix, or the soloed side.
        Public because the waveform must draw the same thing the speakers play;
        two copies of that decision drift. */
    monitorClip,
    get position() { return positionNow(); },

    /**
     * Repeat. Flips the flag on the SOURCE, so switching it on or off mid-play
     * neither restarts nor interrupts anything.
     */
    setLoop(on) {
        looping = !!on;
        if (playSource) { try { playSource.loop = looping; playSource.loopEnd = playDur; } catch (_) { /* ended */ } }
        emit('loop', looping);
        return looping;
    },

    /**
     * Solo one side of the mix. Pressing the engaged one again clears it.
     *
     * This never touches the project — the previous implementation set the
     * other side's fader to zero, which re-rendered (2.2 s), got saved to
     * localStorage, and brought the user back after a reload to a jingle with a
     * silent voice and nothing on screen to explain it.
     *
     * Returns a promise: the buses come from a render, so the first solo of a
     * session costs one, and the page shows its spinner for it.
     */
    setMonitor(which) {
        const want = (which === monitor || !which) ? null : which;
        monitor = want;
        emit('monitor', monitor);
        if (!monitor || (state.result && state.result.buses && state.result.buses[monitor])) {
            swapPlayback();
            return Promise.resolve(monitor);
        }
        return render({ buses: true, quick: true }).then(() => { swapPlayback(); return monitor; });
    },

    /** Is there anything on that side to solo? An empty side is not a bug to hear. */
    monitorHas(which) {
        const p = state.project;
        if (which === 'voice') return !!(cache.voiceRaw && p.voice.gain > 0);
        return !!((p.bed.enabled && p.bed.gain > 0) || (p.fx && p.fx.length));
    },

    render, scheduleRender,

    /* ---- export ---- */

    /**
     * Render `variants` takes and deliver them.
     *
     * Variants re-seed the bed and the effects, so a pack is genuinely
     * different takes of the same script rather than the same file copied — the
     * thing a DJ actually wants when they ask for "a few versions".
     */
    async exportJingle({ onProgress = null, voiceFirst = false } = {}) {
        // A file is always the MIX. Leaving a solo engaged would hand back a
        // render with no buses and a solo button still lit over the full mix.
        if (monitor) api.setMonitor(null);
        exporting = true;
        try { return await api._export({ onProgress, voiceFirst }); } finally { exporting = false; }
    },

    async _export({ onProgress = null, voiceFirst = false } = {}) {
        // The page offers «Озвучить и скачать» when the take does not match the
        // text; doing it here keeps the two steps in ONE user action instead of
        // making a silent mismatch the default outcome.
        if (voiceFirst && api.voiceStale()) {
            await api.speak();
            await render({ stems: state.project.output.stems });
        }
        const p = state.project;
        const items = [];
        const count = Math.max(1, Math.min(10, p.output.variants || 1));
        const baseSeed = p.bed.seed;
        const baseFxSeeds = p.fx.map((f) => f.seed);
        try {
            for (let v = 0; v < count; v++) {
                if (v > 0) {
                    p.bed.seed = baseSeed + v * 1013;
                    p.fx.forEach((f, i) => { f.seed = baseFxSeeds[i] + v * 71; });
                }
                if (onProgress) onProgress(v / count, v);
                const res = await render({ stems: p.output.stems });
                if (!res) continue;
                const enc = await encodeOutput(res.clip, p.output.format, { bitrate: p.output.bitrate });
                items.push({
                    blob: enc.blob,
                    name: jingleFilename({ format: p.format, text: state.voiceText, variant: count > 1 ? v : 0, ext: enc.ext }),
                });
                if (p.output.stems && res.stems) {
                    const stems = await encodeStems(res.stems, p.output.format === 'mp3' ? 'mp3' : 'wav16', CFG.stemNames || {}, { bitrate: p.output.bitrate });
                    for (const s of stems) {
                        items.push({
                            blob: s.blob,
                            name: jingleFilename({ format: p.format, text: state.voiceText, variant: count > 1 ? v : 0, ext: s.ext, suffix: s.name }),
                        });
                    }
                }
            }
        } finally {
            p.bed.seed = baseSeed;
            p.fx.forEach((f, i) => { f.seed = baseFxSeeds[i]; });
        }
        if (onProgress) onProgress(1, count);
        if (!items.length) return { delivered: 0 };
        if (items.length === 1) {
            const ok = deliverOne(items[0].blob, items[0].name);
            return { delivered: ok === false ? 0 : 1, held: ok === false, single: true, name: items[0].name };
        }
        const r = await deliverMany(items, { zipName: jingleFilename({ format: p.format, text: state.voiceText, ext: 'zip' }) });
        return { delivered: items.length, batch: r };
    },

    /** WAV bytes of the current render — used by tests and by "copy to another tool". */
    currentWav() { return state.result ? encodeWav(state.result.clip, { bitDepth: 16 }) : null; },
};

if (typeof window !== 'undefined') {
    window.JingleStudio = api;
    // The page's wiring is a classic inline script and therefore runs BEFORE
    // this deferred module. It waits for this event rather than polling.
    try { document.dispatchEvent(new CustomEvent('jingle-studio:ready', { detail: api })); } catch (_) { /* no CustomEvent on ancient engines */ }
}
export default api;
